import React from 'react';
import { PLAYER_THEME_VARS } from '../../theme';
import { prefersReducedMotion } from '../../../systems/accessibility/motionPrefs';
import { TomeCloseMark, TomeFlourish, TomeSprig } from './TomeOrnaments';
import { TomeFrame, type TomeFramePiece, type TomeFramePreset } from './TomeFrame';
import { TomeSkin, type TomeShapeProps } from './TomeSkin';
import { isDevMode } from '../../../../shared/utils/isDevMode';
import { isTomeVibeName, resolveVibe, tomeVibeStyle, type TomeTexture, type TomeVibe, type TomeVibeInput, type TomeVibeName } from './tomeVibes';
import { TomeVibeContext } from './tomeVibeContext';
import './tome-tokens.css';
import './tome.css';

export interface TomePanelProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'>, TomeShapeProps {
  /** Fixed at the top of the page while the body scrolls; usually a `<TomeTitleBlock>`. */
  header: React.ReactNode;
  /** Optional fixed row under the header (choices, tabs). */
  toolbar?: React.ReactNode;
  /** Optional fixed notice under the header (a refusal, an error); give it `role="alert"`. */
  notice?: React.ReactNode;
  /** The scrolling page body. */
  children?: React.ReactNode;
  /** Whether the children are enclosed in the standard scrolling container. Default true. */
  scrollable?: boolean;
  /** Optional class name for the body container. */
  bodyClassName?: string;
  /**
   * The window's vibe: a named preset from TOME_VIBES (or a TomeVibe) that sets
   * shape, texture, frame and palette at once. Default `default`. Explicit
   * shape, texture and frame props win over it; `vibeOverrides` nudges it.
   */
  vibe?: TomeVibeName | TomeVibe;
  vibeOverrides?: Omit<TomeVibeInput, 'label'>;
  /** Paper texture of the page. Default: the vibe's. */
  texture?: TomeTexture;
  /** Pieces that overhang the cover: a preset or explicit placements. Default: the vibe's. */
  frame?: TomeFramePreset | TomeFramePiece[];
}

/** Dev-only: `globalThis.__TOME_VIBE__ = 'moonfall'` dresses every tome in that vibe (vibe gallery, live tuning). */
function devVibe(): TomeVibeName | undefined {
  if (!isDevMode()) return undefined;
  const name = (globalThis as { __TOME_VIBE__?: unknown }).__TOME_VIBE__;
  return isTomeVibeName(name) ? name : undefined;
}

/**
 * The tome window: a parchment page bound in a woodland cover, dressed by a
 * `vibe` (tomeVibes.ts), both with an organic, seeded silhouette (`edge`, `corners`, `roughness`, `shapeSeed`;
 * the cover takes the corners and a gentler edge), a paper `texture`, and a
 * `frame` of roots, branch and vines overhanging the cover. The gilt double
 * rule follows the page's corners. Chrome only: placement (position, width,
 * stacking) belongs to the caller's class and style, and so do role and
 * aria-label. Sets the player palette variables and re-inks them for
 * parchment, so older pieces that read `--player-*` render dark-on-light.
 * Reduced motion (the OS query and the in-game setting) settles the open
 * animation and every animated piece inside.
 */
export const TomePanel: React.FC<TomePanelProps> = ({
  header, toolbar, notice, children, className = '', style,
  scrollable = true, bodyClassName = '',
  vibe: vibeProp, vibeOverrides, edge, corners, roughness, shapeSeed, texture, frame, ...rest
}) => {
  const worn = devVibe() ?? vibeProp;
  const vibe = React.useMemo(() => resolveVibe(worn, vibeOverrides), [worn, vibeOverrides]);
  const shape = { edge: edge ?? vibe.shape.edge, corners: corners ?? vibe.shape.corners, roughness: roughness ?? vibe.shape.roughness };
  const paper = texture ?? vibe.texture.kind;
  // One silhouette per window: seeded by the caller, else by the window's accessible name.
  const seed = shapeSeed ?? rest['aria-label'] ?? 'tome';
  return <TomeVibeContext.Provider value={vibe}>
    <section {...rest} className={`tome-panel ${className}`} data-vibe={typeof worn === 'string' ? worn : undefined}
      data-reduce-motion={prefersReducedMotion() || undefined} style={{ ...PLAYER_THEME_VARS, ...tomeVibeStyle(vibe), ...style }}>
      <TomeSkin layer="cover" edge={shape.edge === 'clean' ? 'clean' : 'deckled'} corners={shape.corners} roughness={shape.roughness}
        shapeSeed={seed} texture={paper === 'plain' ? 'plain' : 'vellum'} />
      <TomeFrame frame={frame ?? vibe.frame} />
      <div className="tome-panel-sheet">
        <TomeSkin layer="sheet" {...shape} shapeSeed={seed} texture={paper} rule="double" stains={vibe.texture.stains} />
        {header}
        {toolbar && <div className="tome-panel-toolbar">{toolbar}</div>}
        {notice}
        {scrollable ? (
          <div className={`tome-panel-scroll ${bodyClassName}`.trim()}>{children}</div>
        ) : (
          <div className={`tome-panel-body ${bodyClassName}`.trim()}>{children}</div>
        )}
      </div>
    </section>
  </TomeVibeContext.Provider>;
};

export interface TomeTitleBlockProps {
  /** Art for the frame (an NPC portrait, an emblem). A slot: the frame never restyles what is inside it. */
  portrait?: React.ReactNode;
  /** The role line above the title, e.g. "Quests & requests". */
  eyebrow?: React.ReactNode;
  /** The name, in the display face (rendered as the window's h2). */
  title: React.ReactNode;
  /** Optional line under the title (e.g. a gold count). */
  aside?: React.ReactNode;
  /** Small controls drawn just before close (e.g. a window's reset); the frame never restyles them. */
  actions?: React.ReactNode;
  /** Shows the close control when given. */
  onClose?: () => void;
  closeLabel?: string;
}

/** The tome's title block: framed portrait, role line, name, close; a flourish rule beneath. */
export const TomeTitleBlock: React.FC<TomeTitleBlockProps> = ({
  portrait, eyebrow, title, aside, actions, onClose, closeLabel = 'Close',
}) => <header className="tome-title">
  <div className="tome-title-row">
    {portrait !== undefined && <div className="tome-title-portrait">{portrait}</div>}
    <div className="tome-title-text">
      {eyebrow && <span className="tome-title-eyebrow"><TomeSprig />{eyebrow}</span>}
      <h2 className="tome-title-name">{title}</h2>
      {aside && <div className="tome-title-aside">{aside}</div>}
    </div>
    {actions}
    {onClose && <button type="button" className="tome-close" aria-label={closeLabel} onClick={onClose}>
      <TomeCloseMark />
    </button>}
  </div>
  <TomeFlourish />
</header>;
